/**
 * Prev / next buttons for the product card gallery.
 *
 * Replaces the Swiper `Navigation` module of
 * app/components/global/listing/ProductListingItem.vue — the track itself is
 * plain CSS scroll-snap (see src/scss/components/_product-listing-item.scss),
 * so swiping and mouse-wheel scrolling work with this file absent; only the
 * arrows and the ArrowLeft / ArrowRight keys need JS.
 *
 * A single delegated handler on `document`, so cards appended by the Load More
 * button (assets/collection.js, Section Rendering API) and by the theme editor
 * work with no re-initialisation.
 */
(function () {
  'use strict';

  var ARROW_SELECTOR = '[data-listing-prev], [data-listing-next]';

  function slidesOf(track) {
    return Array.prototype.slice.call(track.querySelectorAll('[data-listing-slide]'));
  }

  // The slide whose left edge is nearest the track's current scroll position.
  function currentIndex(track, slides) {
    var position = track.scrollLeft;
    var best = 0;
    var bestDistance = Infinity;

    for (var i = 0; i < slides.length; i++) {
      var distance = Math.abs(slides[i].offsetLeft - track.offsetLeft - position);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    }

    return best;
  }

  // Moves the gallery around `element` one slide in `direction` (-1 / 1).
  // Returns false when there is nothing to move, so callers can leave the
  // event alone.
  function step(element, direction) {
    var gallery = element.querySelector
      ? element.querySelector('[data-listing-gallery]') || element.closest('[data-listing-gallery]')
      : null;
    var track = gallery && gallery.querySelector('[data-listing-track]');
    if (!track) return false;

    var slides = slidesOf(track);
    if (slides.length < 2) return false;

    // Swiper ran with `loop: true`, so the ends wrap rather than dead-end.
    var current = currentIndex(track, slides);
    var next = (current + direction + slides.length) % slides.length;
    var wraps = direction > 0 ? next < current : next > current;

    // No `behavior`, so the track's own `scroll-behavior` decides — which is
    // `smooth` only under `prefers-reduced-motion: no-preference`. A wrap is
    // the exception: smooth-scrolling from the last slide back to the first
    // would rewind through every slide in between (Swiper's loop cloned
    // slides to hide this), so it jumps instead.
    if (wraps) track.style.scrollBehavior = 'auto';
    track.scrollTo({ left: slides[next].offsetLeft - track.offsetLeft });
    if (wraps) track.style.scrollBehavior = '';
    return true;
  }

  // Mobile progress bar (see snippets/product-card.liquid): the thumb's width
  // is set in Liquid; only its position follows the scroll. `scroll` does not
  // bubble, so listen in the capture phase — still one listener for every
  // card, including the ones Load More appends.
  function updateBar(track) {
    var gallery = track.closest('[data-listing-gallery]');
    var thumb = gallery && gallery.querySelector('[data-listing-thumb]');
    if (!thumb) return;

    var range = track.scrollWidth - track.clientWidth;
    var progress = range > 0 ? track.scrollLeft / range : 0;
    var bar = thumb.parentNode;
    var travel = bar.clientWidth - thumb.clientWidth;
    thumb.style.transform = 'translateX(' + Math.round(progress * travel) + 'px)';
  }

  document.addEventListener(
    'scroll',
    function (event) {
      var target = event.target;
      if (target && target.nodeType === 1 && target.hasAttribute('data-listing-track')) updateBar(target);
    },
    true
  );

  // The arrows are aria-hidden spans inside the card <a> (see
  // snippets/product-card.liquid), so the click has to be cancelled or the
  // browser navigates to the product instead of moving a slide.
  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;

    var arrow = target.closest(ARROW_SELECTOR);
    if (!arrow) return;

    event.preventDefault();
    event.stopPropagation();
    step(arrow, arrow.hasAttribute('data-listing-prev') ? -1 : 1);
  });

  // Keyboard equivalent: ArrowLeft / ArrowRight while the card link is
  // focused. The arrows themselves are never focusable.
  document.addEventListener('keydown', function (event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

    var link = event.target && event.target.closest && event.target.closest('[data-listing-link]');
    if (!link) return;

    if (step(link, event.key === 'ArrowLeft' ? -1 : 1)) event.preventDefault();
  });
})();
